import { Link } from "react-router-dom";
import { LucideIcon } from "lucide-react";
import { ServiceTypeSelectorOption } from "../../../types/order";

interface Props {
  to: string;
  icon: LucideIcon;
  overlayIcon?: LucideIcon;
  label: string;
  badge?: string;
  /** A neutral status pill in the top-right corner, e.g. "Aktuell geschlossen".
   *  Grayscale on purpose: the accent colour would highlight a tile that the
   *  status is meant to tone down. The card stays clickable. */
  statusBadge?: string;
  service: ServiceTypeSelectorOption;
  onClickService: (service: ServiceTypeSelectorOption) => void;
  /**
   * Cut a long label with an ellipsis instead of letting it wrap.
   *
   * The card has a fixed height, so a two-line label is CLIPPED mid-letter and
   * reads as a broken screen (see apps/end-user/CLAUDE.md). Every label here
   * used to come from our own i18n files, where we keep them short. A product
   * category's name is typed by the tenant (ADR-0026 §A), so the constraint
   * left our hands — opt-in rather than global, so no existing card's
   * rendering changes.
   */
  singleLine?: boolean;
}

const ServiceCard = ({ to, icon: Icon, overlayIcon: OverlayIcon, label, badge, statusBadge, service, onClickService, singleLine = false }: Props) => {
  return (
    <Link
      onClick={() => onClickService(service)}
      to={to}
      className="no-underline w-full"
    >
      <div className="relative flex h-24 flex-col items-center justify-center gap-2 rounded-lg border-2 border-foreground/10 bg-foreground/5 p-3 text-center transition-colors hover:border-primary active:border-primary">
        {badge && (
          <span className="absolute top-0 left-0 z-10 bg-primary text-onPrimary text-xs font-semibold px-2 py-0.5 rounded-br-lg rounded-tl-lg">
            {badge}
          </span>
        )}
        {statusBadge && (
          <span className="absolute -top-2.5 right-2 z-10 whitespace-nowrap rounded-full bg-foreground px-2 py-0.5 text-[11px] font-semibold leading-tight text-background">
            {statusBadge}
          </span>
        )}
        <span className="relative inline-flex">
          <Icon className="h-8 w-8 text-primary" aria-hidden />
          {OverlayIcon && (
            <span className="absolute -bottom-1.5 -right-1.5 inline-flex rounded-full bg-background p-0.5">
              <OverlayIcon className="h-[18px] w-[18px] text-primary" strokeWidth={3} aria-hidden />
            </span>
          )}
        </span>
        <span
          className={`overflow-hidden text-base font-bold leading-tight tracking-[0.02rem] text-foreground${
            singleLine ? " w-full truncate" : ""
          }`}
          title={singleLine ? label : undefined}
        >
          {label}
        </span>
      </div>
    </Link>
  );
};

export default ServiceCard;
